import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import type { Drama } from '@/types';
import { categoryMeta } from '@/constants/site';
import DramaCard from '@/components/base/DramaCard';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import FilterBar, { type CategoryFilterState } from '@/pages/category/components/FilterBar';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 8;

const defaultFilters: CategoryFilterState = {
  genre: 'all',
  region: 'all',
  year: 'all',
  sort: 'update',
};

export default function CategoryPage() {
  const { type = 'tv' } = useParams();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('keyword') ?? '';

  const [filters, setFilters] = useState<CategoryFilterState>(defaultFilters);
  const [page, setPage] = useState(1);

  const validType: 'tv' | 'movie' | 'all' =
    type === 'tv' || type === 'movie' ? type : 'all';
  const meta =
    validType === 'all'
      ? {
          name: '全部影视',
          desc: '汇集本站全部电视剧与电影作品，支持按体裁、地区、年份与热度多维筛选。',
        }
      : categoryMeta[validType];

  useDocumentMeta({
    title: `${meta.name} - 善良的女秘书影视分类`,
    description: `${meta.desc}${keyword ? ` 当前搜索关键词：${keyword}。` : ''}`,
    keywords: `${meta.name},善良的女秘书,影视分类,剧情介绍,${
      validType === 'movie' ? '电影' : '电视剧'
    }`,
  });

  useEffect(() => {
    setPage(1);
  }, [type, keyword, filters.genre, filters.region, filters.year, filters.sort]);

  const filtered = useMemo(() => {
    const list = (dramas as Drama[]).filter((drama) => {
      if (validType !== 'all' && drama.type !== validType) return false;
      if (filters.genre !== 'all' && !drama.genres.includes(filters.genre)) return false;
      if (filters.region !== 'all' && drama.region !== filters.region) return false;
      if (filters.year !== 'all' && String(drama.year) !== filters.year) return false;
      if (keyword) {
        const haystack = [
          drama.title,
          ...drama.actors,
          ...drama.genres,
          drama.synopsis,
        ].join(' ');
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });

    switch (filters.sort) {
      case 'heat':
        return list.sort((a, b) => b.heat - a.heat);
      case 'rating':
        return list.sort((a, b) => b.rating - a.rating);
      default:
        return list.sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1));
    }
  }, [validType, filters, keyword]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice(0, page * PAGE_SIZE);
  const canLoadMore = page < totalPages;

  const breadcrumbName = validType === 'movie' ? '电影' : validType === 'tv' ? '电视剧' : '全部影视';

  return (
    <div className="w-full px-4 py-8 md:px-8 lg:px-12">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="whitespace-nowrap transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="whitespace-nowrap text-foreground-800">{breadcrumbName}</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {meta.name}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          {meta.desc}
        </p>
        {keyword && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-accent-100 px-3 py-1.5 text-sm text-accent-900">
            <i className="ri-search-line"></i>
            搜索关键词：<span className="font-medium">{keyword}</span>
            <Link to={`/category/${validType}`} className="ml-1 cursor-pointer hover:underline">
              清除
            </Link>
          </div>
        )}
      </div>

      <FilterBar
        filters={filters}
        onChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
        onReset={() => setFilters(defaultFilters)}
        resultCount={filtered.length}
      />

      <div className="mt-6">
        {visible.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-5">
              {visible.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />

            {canLoadMore && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title="没有找到匹配的影视作品"
            description="试试调整或重置筛选条件，看看其他精彩的影视内容。"
            actionLabel="重置筛选"
            onAction={() => setFilters(defaultFilters)}
          />
        )}
      </div>
    </div>
  );
}